import { useEffect, useRef, useState } from "react";

const MOVE_KEYS = ["arrowup", "arrowdown", "arrowleft", "arrowright", "w", "a", "s", "d"];

export function useKeyboardMovement({ initial, bounds, speed = 0.85, enabled = true }) {
  const [pos, setPos] = useState(initial);
  const [facing, setFacing] = useState("down");
  const [moving, setMoving] = useState(false);
  const keys = useRef({});
  const raf = useRef();
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // mirror of pos + a walk-toward target (for tap-to-move)
  const posRef = useRef(initial);
  const target = useRef(null);
  useEffect(() => {
    posRef.current = pos;
  }, [pos]);

  const clamp = (x, y) => ({
    x: Math.max(bounds.minX, Math.min(bounds.maxX, x)),
    y: Math.max(bounds.minY, Math.min(bounds.maxY, y)),
  });

  // Public: walk the avatar toward a point (percent coords within the scene).
  const moveTo = (x, y) => {
    if (!enabledRef.current) return;
    target.current = clamp(x, y);
  };

  useEffect(() => {
    const down = (e) => {
      // never hijack typing in form fields or when movement is disabled (e.g. a modal/game is open)
      const t = e.target;
      const typing = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
      if (typing || !enabledRef.current) return;
      const k = e.key.toLowerCase();
      if (MOVE_KEYS.includes(k)) {
        keys.current[k] = true;
        e.preventDefault();
      }
    };
    const up = (e) => {
      keys.current[e.key.toLowerCase()] = false;
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, []);

  useEffect(() => {
    const loop = () => {
      if (enabledRef.current) {
        const k = keys.current;
        let dx = 0;
        let dy = 0;
        if (k["arrowup"] || k["w"]) dy -= 1;
        if (k["arrowdown"] || k["s"]) dy += 1;
        if (k["arrowleft"] || k["a"]) dx -= 1;
        if (k["arrowright"] || k["d"]) dx += 1;

        if (dx !== 0 || dy !== 0) {
          // keyboard input always overrides any tap target
          target.current = null;
          if (Math.abs(dy) >= Math.abs(dx)) setFacing(dy < 0 ? "up" : "down");
          else setFacing(dx < 0 ? "left" : "right");
          setMoving(true);
          setPos((p) => clamp(p.x + dx * speed, p.y + dy * speed));
        } else if (target.current) {
          // tap-to-move: step toward the target point
          const p = posRef.current;
          const t = target.current;
          const ddx = t.x - p.x;
          const ddy = t.y - p.y;
          const dist = Math.hypot(ddx, ddy);
          if (dist <= speed * 1.2) {
            target.current = null;
            setMoving(false);
          } else {
            if (Math.abs(ddy) >= Math.abs(ddx)) setFacing(ddy < 0 ? "up" : "down");
            else setFacing(ddx < 0 ? "left" : "right");
            setMoving(true);
            setPos((pp) => clamp(pp.x + (ddx / dist) * speed, pp.y + (ddy / dist) * speed));
          }
        } else {
          setMoving(false);
        }
      } else {
        target.current = null;
      }
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speed, bounds.minX, bounds.maxX, bounds.minY, bounds.maxY]);

  return { pos, setPos, facing, moving, moveTo };
}
